import { Backdrop, Box, Button, Card, FormControl, FormControlLabel, FormHelperText, FormLabel, InputLabel, MenuItem, Radio, RadioGroup, Select, Stack, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import Scrollbars from "react-custom-scrollbars-2";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { putStudentInfo } from "./studentSlice";

const modalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    width: { xs: "90%", md: "40%" },
    maxHeight: "90%",
    boxShadow: 24,
};

export default function StudentInfoWarning({ isInfoComplete }) {
    const dispatch = useDispatch();
    const location = useLocation();

    const { appLanguage, resourceFinal } = useSelector(
        (state) => state.settings.resource.resources
    );
    const {
        studentInfoSettings,
        studentInfoSettingsStatus,
        putStudentInfoStatus,
    } = useSelector((state) => state.student);

    const {
        privateInfoRequired,
        privateEmailRequired,
        privateEmail,
        privatePhoneRequired,
        privatePhone,
        workPhoneRequired,
        workPhone,
        ibanRequired,
        iban,
        pokRequired,
        pok,
        wintersportInfoRequired,
        allergy,
        skiExperience,
        sport,
        medication,
        emergencyContactName,
        emergencyContactNumber,
        shoeSizeEU,
        height,
        weight,
        ownedMaterial,
    } = studentInfoSettings;

    const [saveFailed, setSaveFailed] = useState(false);
    const [disabled, setDisabled] = useState(false);

    const [formState, setFormState] = useState({
        email: { value: null, error: null },
        mobilePhone: { value: null, error: null },
        anyPhone: { value: null, error: null },
        iban: { value: null, error: null },
        pok: { value: null, error: null },
        allergy: { value: null, error: null },
        skiExperience: { value: null, error: null },
        sport: { value: null, error: null },
        medication: { value: null, error: null },
        emergencyContactName: { value: null, error: null },
        emergencyContactNumber: { value: null, error: null },
        shoeSizeEU: { value: null, error: null },
        height: { value: null, error: null },
        weight: { value: null, error: null },
        ownedMaterial: { value: null, error: null },
    });

    const isValidAnyPhone = (v) =>
        /^((\\+|00(\\s|\\s?\\-\\s?)?)31(\\s|\\s?\\-\\s?)?(\\(0\\)[\\-\\s]?)?|0)[1-9]((\\s|\\s?\\-\\s?)?[0-9]){8}$/.test(v);
    const isValidEmail = (v) =>
        /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(v);
    const isValidMobilePhone = (v) =>
        /^\(?([+]31|0031|0)-?6(\s?|-)([0-9]\s{0,3}){8}$/.test(v);
    const isValidIban = (v) =>
        /^NL[0-9]{2}[A-Z0-9]{4}[0-9]{10}$/.test(v);
    const isValidValue = (v) => v !== null && v !== undefined && v !== "";

    const validators = {
        email: isValidEmail,
        mobilePhone: isValidMobilePhone,
        anyPhone: isValidAnyPhone,
        iban: isValidIban,
        emergencyContactNumber: isValidAnyPhone,
        default: isValidValue,
    };

    const handleValueChange = (field, value, required, errorMessage) => {
        const validator = validators[field] || validators.default;

        let error = null;

        if (required && value.length > 3 && !validator(value)) {
            error = errorMessage;
        }

        setFormState(prev => ({
            ...prev,
            [field]: {
                value,
                error,
            },
        }));
    };

    const submitValues = () => {
        const payload = {};

        Object.entries(formState).forEach(([field, { value, error }]) => {
            if (value !== null && !error) {
                payload[field] = value;
            }
        });

        if (Object.keys(payload).length === 0) return;

        setDisabled(true);

        dispatch(putStudentInfo(payload));
    };

    useEffect(() => {
        if (putStudentInfoStatus === "success") {
            window.location.reload();
        } else if (putStudentInfoStatus === "failed") {
            setSaveFailed(true);
            setDisabled(false);
        }
    }, [putStudentInfoStatus]);

    if (
        location.pathname.toLowerCase() === "/account" ||
        studentInfoSettingsStatus !== "success" ||
        !(privateInfoRequired && !isInfoComplete)
    ) {
        return null;
    }

    return (
        <Backdrop open sx={{ zIndex: (t) => t.zIndex.drawer + 1 }}>
            <Box sx={modalStyle}>
                <Card sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', p: 2 }}>
                    <Scrollbars autoHeight autoHeightMin="100%" autoHeightMax="100%" autoHideTimeout={1000} autoHideDuration={200}>
                        <Stack spacing={1} alignItems="center">
                            <Typography variant="h6">
                                {resourceFinal[appLanguage].messages.PRIVATE_INFO_WARNING}
                            </Typography>

                            <TextFieldFragment
                                state={formState}
                                field="email"
                                required={privateEmailRequired}
                                existingValue={privateEmail}
                                label={resourceFinal[appLanguage].words.USER_DATA_EMAIL_PRIVATE}
                                onChange={(e) =>
                                    handleValueChange(
                                        "email",
                                        e.target.value,
                                        privateEmailRequired,
                                        resourceFinal[appLanguage].messages.INVALID_EMAIL
                                    )
                                }
                            />

                            <TextFieldFragment
                                state={formState}
                                field="mobilePhone"
                                required={privatePhoneRequired}
                                existingValue={privatePhone}
                                label={resourceFinal[appLanguage].words.USER_DATA_PHONENUMBER}
                                onChange={(e) =>
                                    handleValueChange(
                                        "mobilePhone",
                                        e.target.value,
                                        privatePhoneRequired,
                                        resourceFinal[appLanguage].messages.INVALID_MOBILE_PHONE
                                    )
                                }
                            />

                            <TextFieldFragment
                                state={formState}
                                field="anyPhone"
                                required={workPhoneRequired}
                                existingValue={workPhone}
                                label={resourceFinal[appLanguage].words.USER_DATA_PHONENUMBER_ICE}
                                onChange={(e) =>
                                    handleValueChange(
                                        "anyPhone",
                                        e.target.value,
                                        workPhoneRequired,
                                        resourceFinal[appLanguage].messages.INVALID_PHONE
                                    )
                                }
                            />

                            <TextFieldFragment
                                state={formState}
                                field="iban"
                                required={ibanRequired}
                                existingValue={iban}
                                label={resourceFinal[appLanguage].words.USER_DATA_IBAN}
                                onChange={(e) =>
                                    handleValueChange(
                                        "iban",
                                        e.target.value,
                                        ibanRequired,
                                        resourceFinal[appLanguage].messages.INVALID_IBAN
                                    )
                                }
                            />

                            <RadioGroupFragment
                                state={formState}
                                field="pok"
                                required={pokRequired}
                                existingValue={pok}
                                label={resourceFinal[appLanguage].words.USER_DATA_POK}
                                onChange={(e) =>
                                    handleValueChange(
                                        "pok",
                                        e.target.value,
                                        pokRequired,
                                        resourceFinal[appLanguage].messages.INVALID_POK
                                    )
                                }
                            />

                            <TextFieldFragment
                                state={formState}
                                field="allergy"
                                required={wintersportInfoRequired}
                                existingValue={allergy}
                                label={resourceFinal[appLanguage].words.USER_DATA_ALLERGY}
                                onChange={(e) =>
                                    handleValueChange(
                                        "allergy",
                                        e.target.value,
                                        wintersportInfoRequired,
                                        resourceFinal[appLanguage].messages.INVALID_VALUE
                                    )
                                }
                            />

                            <DropdownSelectFragment
                                state={formState}
                                field="skiExperience"
                                required={wintersportInfoRequired}
                                existingValue={skiExperience}
                                label={resourceFinal[appLanguage].words.USER_DATA_SKIEXPERIENCE}
                                onChange={(e) =>
                                    handleValueChange(
                                        "skiExperience",
                                        e.target.value,
                                        wintersportInfoRequired,
                                        resourceFinal[appLanguage].messages.INVALID_VALUE
                                    )
                                }
                                menuItems={[
                                    <MenuItem key="0" value={resourceFinal[appLanguage].words.USER_DATA_SKIEXPERIENCE_NO}>
                                        {resourceFinal[appLanguage].words.USER_DATA_SKIEXPERIENCE_NO}
                                    </MenuItem>,
                                    <MenuItem key="1" value={resourceFinal[appLanguage].words.USER_DATA_SKIEXPERIENCE_YES1}>
                                        {resourceFinal[appLanguage].words.USER_DATA_SKIEXPERIENCE_YES1}
                                    </MenuItem>,
                                    <MenuItem key="2" value={resourceFinal[appLanguage].words.USER_DATA_SKIEXPERIENCE_YES2}>
                                        {resourceFinal[appLanguage].words.USER_DATA_SKIEXPERIENCE_YES2}
                                    </MenuItem>,
                                    <MenuItem key="3" value={resourceFinal[appLanguage].words.USER_DATA_SKIEXPERIENCE_YES3}>
                                        {resourceFinal[appLanguage].words.USER_DATA_SKIEXPERIENCE_YES3}
                                    </MenuItem>,
                                    <MenuItem key="4" value={resourceFinal[appLanguage].words.USER_DATA_SKIEXPERIENCE_YES4}>
                                        {resourceFinal[appLanguage].words.USER_DATA_SKIEXPERIENCE_YES4}
                                    </MenuItem>,
                                ]}
                            />

                            <DropdownSelectFragment
                                state={formState}
                                field="sport"
                                required={wintersportInfoRequired}
                                existingValue={sport}
                                label={resourceFinal[appLanguage].words.USER_DATA_SPORT}
                                onChange={(e) =>
                                    handleValueChange(
                                        "sport",
                                        e.target.value,
                                        wintersportInfoRequired,
                                        resourceFinal[appLanguage].messages.INVALID_VALUE
                                    )
                                }
                                menuItems={[
                                    <MenuItem key="0" value={resourceFinal[appLanguage].words.USER_DATA_SPORT_1}>
                                        {resourceFinal[appLanguage].words.USER_DATA_SPORT_1}
                                    </MenuItem>,
                                    <MenuItem key="1" value={resourceFinal[appLanguage].words.USER_DATA_SPORT_2}>
                                        {resourceFinal[appLanguage].words.USER_DATA_SPORT_2}
                                    </MenuItem>,
                                ]}
                            />

                            <TextFieldFragment
                                state={formState}
                                field="medication"
                                required={wintersportInfoRequired}
                                existingValue={medication}
                                label={resourceFinal[appLanguage].words.USER_DATA_MEDICATION}
                                onChange={(e) =>
                                    handleValueChange(
                                        "medication",
                                        e.target.value,
                                        wintersportInfoRequired,
                                        resourceFinal[appLanguage].messages.INVALID_VALUE
                                    )
                                }
                            />

                            <TextFieldFragment
                                state={formState}
                                field="emergencyContactName"
                                required={wintersportInfoRequired}
                                existingValue={emergencyContactName}
                                label={resourceFinal[appLanguage].words.USER_DATA_EMERGENCY_CONTACT_NAME}
                                onChange={(e) =>
                                    handleValueChange(
                                        "emergencyContactName",
                                        e.target.value,
                                        wintersportInfoRequired,
                                        resourceFinal[appLanguage].messages.INVALID_VALUE
                                    )
                                }
                            />

                            <TextFieldFragment
                                state={formState}
                                field="emergencyContactNumber"
                                required={wintersportInfoRequired}
                                existingValue={emergencyContactNumber}
                                label={resourceFinal[appLanguage].words.USER_DATA_EMERGENCY_CONTACT_NUMBER}
                                onChange={(e) =>
                                    handleValueChange(
                                        "emergencyContactNumber",
                                        e.target.value,
                                        wintersportInfoRequired,
                                        resourceFinal[appLanguage].messages.INVALID_PHONE
                                    )
                                }
                            />

                            <NumberTextFieldFragment
                                state={formState}
                                field="shoeSizeEU"
                                required={wintersportInfoRequired}
                                existingValue={shoeSizeEU}
                                step={0.5}
                                min={0}
                                max={100}
                                label={resourceFinal[appLanguage].words.USER_DATA_SHOE_SIZE}
                                onChange={(e) =>
                                    handleValueChange(
                                        "shoeSizeEU",
                                        e.target.value,
                                        wintersportInfoRequired,
                                        resourceFinal[appLanguage].messages.INVALID_VALUE
                                    )
                                }
                            />

                            <NumberTextFieldFragment
                                state={formState}
                                field="height"
                                required={wintersportInfoRequired}
                                existingValue={height}
                                step={0.1}
                                min={0}
                                max={999}
                                label={resourceFinal[appLanguage].words.USER_DATA_HEIGHT}
                                onChange={(e) =>
                                    handleValueChange(
                                        "height",
                                        e.target.value,
                                        wintersportInfoRequired,
                                        resourceFinal[appLanguage].messages.INVALID_VALUE
                                    )
                                }
                            />

                            <NumberTextFieldFragment
                                state={formState}
                                field="weight"
                                required={wintersportInfoRequired}
                                existingValue={weight}
                                step={0.1}
                                min={0}
                                max={999}
                                label={resourceFinal[appLanguage].words.USER_DATA_WEIGHT}
                                onChange={(e) =>
                                    handleValueChange(
                                        "weight",
                                        e.target.value,
                                        wintersportInfoRequired,
                                        resourceFinal[appLanguage].messages.INVALID_VALUE
                                    )
                                }
                            />

                            <DropdownSelectFragment
                                state={formState}
                                field="ownedMaterial"
                                required={wintersportInfoRequired}
                                existingValue={ownedMaterial}
                                label={resourceFinal[appLanguage].words.USER_DATA_OWNED_MATERIAL}
                                onChange={(e) =>
                                    handleValueChange(
                                        "ownedMaterial",
                                        e.target.value,
                                        wintersportInfoRequired,
                                        resourceFinal[appLanguage].messages.INVALID_VALUE
                                    )
                                }
                                menuItems={[
                                    <MenuItem key="0" value={resourceFinal[appLanguage].words.USER_DATA_OWNED_MATERIAL_1}>
                                        {resourceFinal[appLanguage].words.USER_DATA_OWNED_MATERIAL_1}
                                    </MenuItem>,
                                    <MenuItem key="1" value={resourceFinal[appLanguage].words.USER_DATA_OWNED_MATERIAL_2}>
                                        {resourceFinal[appLanguage].words.USER_DATA_OWNED_MATERIAL_2}
                                    </MenuItem>,
                                    <MenuItem key="2" value={resourceFinal[appLanguage].words.USER_DATA_OWNED_MATERIAL_3}>
                                        {resourceFinal[appLanguage].words.USER_DATA_OWNED_MATERIAL_3}
                                    </MenuItem>,
                                    <MenuItem key="3" value={resourceFinal[appLanguage].words.USER_DATA_OWNED_MATERIAL_4}>
                                        {resourceFinal[appLanguage].words.USER_DATA_OWNED_MATERIAL_4}
                                    </MenuItem>,
                                ]}
                            />

                            {saveFailed && (
                                <Typography color="error">
                                    {resourceFinal[appLanguage].messages.ERROR_SAVE_FAILED}
                                </Typography>
                            )}

                            <Button
                                disabled={disabled}
                                onClick={submitValues}
                                variant="outlined"
                                color="secondary"
                            >
                                {resourceFinal[appLanguage].words.SAVE}
                            </Button>
                        </Stack>
                    </Scrollbars>
                </Card>
            </Box>
        </Backdrop>
    );
}

const TextFieldFragment = ({ state, field, required, existingValue, onChange, label }) => {
    if (!required || existingValue) return null;
    const { error } = state[field];

    return (
        <TextField
            fullWidth
            error={Boolean(error)}
            helperText={error}
            onChange={onChange}
            label={label}
        />
    );
};

const NumberTextFieldFragment = ({ state, field, required, existingValue, onChange, label, step, min, max }) => {
    if (!required || existingValue) return null;
    const { error } = state[field];

    return (
        <TextField
            type="number"
            inputProps={{ step, min, max }}
            fullWidth
            error={Boolean(error)}
            helperText={error}
            onChange={onChange}
            label={label}
        />
    );
};

const DropdownSelectFragment = ({ state, field, required, existingValue, onChange, label, menuItems }) => {
    if (!required || existingValue) return null;
    const { error, value } = state[field];

    return (
        <FormControl fullWidth error={Boolean(error)}>
            <InputLabel>{label}</InputLabel>
            <Select value={value ?? ""} onChange={onChange} label={label}>
                {menuItems}
            </Select>
            <FormHelperText>{error}</FormHelperText>
        </FormControl>
    );
};

const RadioGroupFragment = ({ state, field, required, existingValue, onChange, label }) => {
    if (!required || existingValue === "True" || existingValue === "False") return null;
    const { error } = state[field];

    return (
        <FormControl error={Boolean(error)}>
            <FormLabel>{label}</FormLabel>
            <RadioGroup onChange={onChange}>
                <FormControlLabel value="True" control={<Radio />} label="Yes" />
                <FormControlLabel value="False" control={<Radio />} label="No" />
            </RadioGroup>
            <FormHelperText>{error}</FormHelperText>
        </FormControl>
    );
};